import React from 'react'
import Container from '@material-ui/core/Container'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_RADIUS,
  PROMO_CALLOUT_BACKGROUND,
  PROMO_CALLOUT_BORDER_RADIUS,
} from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'stretch',
    padding: theme.spacing(2),
    backgroundColor: CALLOUT_BACKGROUND,
    borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
    color: CALLOUT_FOREGROUND,
  },
  promoRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: PROMO_CALLOUT_BACKGROUND,
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    color: CALLOUT_FOREGROUND,
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    paddingRight: theme.spacing(2),
    flexShrink: 0,
  },
  icon: {
    color: CALLOUT_FOREGROUND,
    fontSize: 20,
  },
  divider: {
    marginRight: theme.spacing(2),
    backgroundColor: OUTLINED_INPUT_BORDER,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
  },
  promoContent: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: CALLOUT_FOREGROUND,
    marginBottom: theme.spacing(0.5),
  },
  promoTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.25,
    color: CALLOUT_FOREGROUND,
    marginBottom: theme.spacing(0.5),
  },
  body: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: CALLOUT_FOREGROUND,
    '& .MuiTypography-body2': {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: CALLOUT_FOREGROUND,
    },
  },
  promoBody: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: CALLOUT_FOREGROUND,
    '& .MuiTypography-body2': {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: CALLOUT_FOREGROUND,
    },
  },
  actionRow: {
    marginTop: theme.spacing(1),
    '& .MuiButton-root': {
      minWidth: 0,
      padding: 0,
      fontWeight: 600,
      textTransform: 'none',
      justifyContent: 'flex-start',
    },
  },
  promoAction: {
    flexShrink: 0,
    '& .MuiButton-root': {
      borderRadius: 24,
      fontWeight: 600,
      textTransform: 'none',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      whiteSpace: 'nowrap',
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
}))

export default function InfoAlert({
  children,
  title,
  action,
  maxWidth = false,
  variant = 'default',
  hideIcon = false,
  className,
  ...rest
}) {
  const classes = useStyles()
  const isPromo = variant === 'promo'

  return (
    <Container maxWidth={maxWidth} disableGutters>
      <Box
        className={clsx(isPromo ? classes.promoRoot : classes.root, className)}
        role="alert"
        {...rest}
      >
        {isPromo ? (
          <>
            <Box className={classes.promoContent}>
              {title ? (
                <Typography className={classes.promoTitle} component="p">
                  {title}
                </Typography>
              ) : null}
              <Box className={classes.promoBody}>{children}</Box>
            </Box>
            {action ? (
              <Box className={classes.promoAction}>{action}</Box>
            ) : null}
          </>
        ) : (
          <>
            {!hideIcon ? (
              <>
                <Box className={classes.iconWrapper}>
                  <InfoOutlinedIcon className={classes.icon} />
                </Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  className={classes.divider}
                />
              </>
            ) : null}
            <Box className={classes.content}>
              {title ? (
                <Typography className={classes.title} component="p">
                  {title}
                </Typography>
              ) : null}
              <Box className={classes.body}>{children}</Box>
              {action ? (
                <Box className={classes.actionRow}>{action}</Box>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </Container>
  )
}
